import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { post } from "../../utils/apiManager";
import { toast } from "react-toastify";
import { baseURL } from "../../config/config";
import { State, City } from 'country-state-city';
import PhoneInput from "../../components/Auth/PhoneNumber";

const inputStyle = (hasError) => ({
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: `1.5px solid ${hasError ? '#AD2429' : '#e2e8f0'}`,
  fontSize: 14, color: '#0a1628', background: '#fff',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  transition: 'border-color .15s',
});

const labelStyle = {
  fontSize: 13, fontWeight: 600, color: '#3a4a6a',
  marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4,
};

const fieldWrap = { marginBottom: 20 };
const errorMsg = { fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' };

const Field = ({ label, required, error, children }) => (
  <div style={fieldWrap}>
    <label style={labelStyle}>
      {label} {required && <span style={{ color: '#AD2429' }}>*</span>}
    </label>
    {children}
    {error && <span style={errorMsg}>{error}</span>}
  </div>
);

const Add = () => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm();
  const Navigate = useNavigate();
  const allStates = State.getStatesOfCountry('US');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  const stateChangeHandler = (state) => {
    if (state !== "") {
      const { isoCode } = JSON.parse(state);
      setCities(City.getCitiesOfState('US', isoCode));
    }
  };

  const onSubmit = (data) => {
    setLoading(true);
    if (data?.state !== "" && JSON?.parse(data?.state)) data.state = JSON.parse(data.state).name;
    post(`${baseURL}/company/create`, data)
      .then((res) => {
        setLoading(false);
        if (res?.status) Navigate("/admin/user");
        else toast.error(res?.message);
      })
      .catch((err) => { setLoading(false); toast.error(err?.response?.data?.error); });
  };

const dropdownArrowSvg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235a6a8a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E`;

const selectStyle = (hasError) => ({
  ...inputStyle(hasError),
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  cursor: 'pointer',
  backgroundImage: `url("${dropdownArrowSvg}")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '16px 16px',
  paddingRight: '36px',
});

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 28px', borderBottom: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>Add Company</h3>
            <p style={{ fontSize: 13, color: '#9aa5c0', margin: '3px 0 0' }}>Create a new auction company account</p>
          </div>
          <button onClick={() => Navigate('/admin/user')} style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            ← Back
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '28px' }}>

          {/* Section: Personal */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#AD2429', marginBottom: 16 }}>Contact Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 8 }}>
            <Field label="First Name" required error={errors.firstName && 'Required'}>
              <input placeholder="First Name" type="text" style={inputStyle(errors.firstName)} {...register("firstName", { required: true })} />
            </Field>
            <Field label="Last Name" required error={errors.lastName && 'Required'}>
              <input placeholder="Last Name" type="text" style={inputStyle(errors.lastName)} {...register("lastName", { required: true })} />
            </Field>
            <Field label="Company Name" required error={errors.company_name && 'Required'}>
              <input placeholder="Company Name" type="text" style={inputStyle(errors.company_name)} {...register("company_name", { required: true })} />
            </Field>
            <Field label="Phone" required error={errors.phone && errors.phone.message}>
              <PhoneInput name="phone" placeholder="Phone" watch={watch} setValue={setValue} errors={errors} register={register} />
            </Field>
            <Field label="Cell Phone" required error={errors.cell_phone && errors.cell_phone.message}>
              <PhoneInput name="cell_phone" placeholder="Cell Phone" watch={watch} setValue={setValue} errors={errors} register={register} />
            </Field>
            <Field label="Email" required error={errors.email && 'Required'}>
              <input placeholder="email@company.com" type="email" style={inputStyle(errors.email)} {...register("email", { required: true })} />
            </Field>
          </div>

          {/* Section: Business */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#AD2429', marginBottom: 16, marginTop: 8 }}>Business Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 8 }}>
            <Field label="Company Type" required error={errors.companyType && 'Required'}>
              <select style={selectStyle(errors.companyType)} {...register("companyType", { required: true })}>
                <option value="">Select Company Type</option>
                <option value="Auction">Auction</option>
                <option value="Estate Sales">Estate Sales</option>
                <option value="Other Bussiness">Other Business</option>
                <option value="Private Estate Sales">Private Estate Sales</option>
              </select>
            </Field>
            <Field label="Website">
              <input placeholder="https://yourwebsite.com" type="text" style={inputStyle(false)} {...register("website")} />
            </Field>
            <Field label="Apt Suite">
              <input placeholder="Suite 100" type="text" style={inputStyle(false)} {...register("address_apt_suite")} />
            </Field>
          </div>

          {/* Section: Address */}
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#AD2429', marginBottom: 16, marginTop: 8 }}>Address</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 8 }}>
            <Field label="Address" required error={errors.address && 'Required'}>
              <textarea placeholder="Enter street address" style={{ ...inputStyle(errors.address), resize: 'vertical', minHeight: 80 }} {...register("address", { required: true })} />
            </Field>
            <Field label="State" required error={errors.state && 'Required'}>
              <select style={selectStyle(errors.state)} {...register('state', { required: true, onChange: (e) => stateChangeHandler(e.target.value) })}>
                <option value="">Select State</option>
                {allStates.map((val, i) => <option key={i} value={JSON.stringify(val)}>{val.name}</option>)}
              </select>
            </Field>
            <Field label="City" required error={errors.city && errors.city.message}>
              <select style={selectStyle(errors.city)} {...register('city', { required: 'Required' })}>
                <option value="">Select City</option>
                {cities.map((val, i) => <option key={i} value={val.name}>{val.name}</option>)}
              </select>
            </Field>
            <Field label="Zip Code" required error={errors.zip_code && 'Required'}>
              <input placeholder="Zip Code" type="text" style={inputStyle(errors.zip_code)} {...register("zip_code", { required: true })} />
            </Field>
          </div>

          {/* Submit */}
          <div style={{ paddingTop: 16, borderTop: '1px solid #edf0f7', display: 'flex', gap: 12 }}>
            <button type="submit" disabled={loading} style={{ background: '#AD2429', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 28px', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Saving...' : 'Create Company'}
            </button>
            <button type="button" onClick={() => Navigate('/admin/user')} style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Add;
